import { useEffect, useState } from "react";

import { api } from "@/utils/Axios";

const PRODUCTS_STORAGE_KEY = "Products";
const POLL_INTERVAL_MS = 300;
const LOAN_PACKAGES_ENDPOINT = "/services/loan-calculator/packages";

/**
 * Resolves a single product out of the `Products` sessionStorage cache that
 * `_app` populates on idle, then loads its loan-calculator packages.
 *
 * This was duplicated verbatim in every `/products/*` page. Behaviour is
 * unchanged: poll sessionStorage every 300ms, stop on the first hit, and only
 * request packages when the product has both a `crmid` and `hasPlan`.
 *
 * @param {string|number} productId id of the product to look up
 * @returns {{ targetProduct: object|null, data: any }}
 */
export function useProductLoanPackages(productId) {
  const [targetProduct, setTargetProduct] = useState(null);
  const [data, setData] = useState();

  useEffect(() => {
    let cancelled = false;

    const interval = setInterval(() => {
      const productsStr = sessionStorage.getItem(PRODUCTS_STORAGE_KEY);
      if (!productsStr) return;

      clearInterval(interval);

      const products = JSON.parse(productsStr);
      const foundProduct = products?.find((p) => p.id === productId);

      setTargetProduct(foundProduct);

      const crmId = foundProduct?.crmid;
      const hasPlan = foundProduct?.hasPlan;

      if (!crmId || !hasPlan) {
        setData(null);
        return;
      }

      api
        .post(LOAN_PACKAGES_ENDPOINT, { crmId: crmId.toString() })
        /* Guard against resolving after unmount — the request outlives the page. */
        .then((res) => {
          if (!cancelled) setData(res?.data);
        })
        .catch(() => {
          if (!cancelled) setData(null);
        });
    }, POLL_INTERVAL_MS);

    return () => {
      cancelled = true;
      clearInterval(interval);
    };
    /*
     * Intentionally mount-only, matching the previous implementation: the
     * product id is a build-time constant coming from the locale dictionary.
     */
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { targetProduct, data };
}
